import commentAxios from 'api/commentsAxios'
import React, { Fragment, useCallback, useEffect, useState } from 'react'
import styled from 'styled-components'
import { Button } from 'react-bootstrap'
import moment from 'moment'
import {
  FieldWrapper,
  Label,
  RemoveButton,
  Select
} from '../ContentStructure/Components'
import RepliesTable from './RepliesTable'
import Collapse from 'ui/bend/animations/Collapse'
import { LeftChevron } from 'images'

const TableWrapper = styled.div`
  padding: 1.5rem;
  .td-date {
    white-space: nowrap;
  }
  p {
    margin: 0;
  }
  th {
    color: #6c757d;
    cursor: default;
    user-select: none;
  }
  .blue {
    color: #3985b9;
    user-select: none;
    cursor: pointer;
  }
  .pointer {
    cursor: pointer;
  }
  button {
    user-select: none;
  }
  .flex-box {
    display: flex;
    gap: 8px;
    align-items: center;
    button {
      border-radius: 6px !important;
      height: 20px;
      width: 20px;
    }
  }
  .p-0 {
    padding: 0 8px;
    &.open {
      padding: 8px;
    }
  }
`

const Main = styled.div`
  .select-sort {
    width: fit-content;
    padding: 0 1.5rem;
    label {
      margin-bottom: 0px;
    }
    select {
      padding: 6px;
    }
  }
`

const ManageContent = ({ id = '', title, type }) => {
  const [sortConfig, setSortConfig] = useState({
    key: 'newest',
    desc: true,
    lastKey: null,
    loadMore: false,
    content_type: 'CLASS'
  })
  const [data, setData] = useState([])
  const [lastKey, setLastKey] = useState(null)
  const [loading, setLoading] = useState(false)
  const [openSections, setOpenSections] = useState({})
  const [total, setTotal] = useState(0)

  const toggleSort = (key) => {
    setLastKey(null)
    setSortConfig((state) => {
      return { ...state, key, desc: true, lastKey: null, loadMore: false }
    })
  }
  const fetchData = useCallback(
    async ({ key = 'newest', lastKey, loadMore = false }) => {
      try {
        setLoading(true)
        const res = await commentAxios.post('/comments', {
          sort: key,
          content_type: type,
          content_id: id,
          limit: 10,
          last_evaluated_key: lastKey,
          include_all: true
        })
        const data = res.data?.comments || []
        setTotal(res.data?.commentCount || 0)
        setData((state) => {
          if (loadMore) {
            return [...state, ...data]
          }
          return data
        })
        setLastKey(res?.data?.lastEvaluatedKey)
      } catch (err) {
        console.log(err)
        setData([])
      } finally {
        setLoading(false)
      }
    },
    [type, id]
  )

  useEffect(() => {
    fetchData(sortConfig)
  }, [fetchData, sortConfig])

  const handlePagination = () => {
    setSortConfig((prev) => ({
      ...prev,
      lastKey,
      loadMore: true
    }))
  }

  const handleDelete = (comment_id) => {
    const confirmed = window.confirm(
      `Are you sure you want to delete this comment`
    )
    if (confirmed) {
      commentAxios
        .post('/content-interaction/delete', {
          content_type: type,
          content_id: id,
          comment_id
        })
        .then((res) => {
          if (res.data?.success) {
            setLastKey(null)
            setSortConfig((state) => ({
              ...state,
              lastKey: null,
              loadMore: false
            }))
          }
        })
        .catch((err) => {
          console.log(err)
        })
    }
  }

  return (
    <Main>
      <FieldWrapper className='select-sort'>
        <Label>Sort</Label>
        <Select
          value={sortConfig.key}
          onChange={(e) => toggleSort(e.target.value)}
        >
          <option value='newest'>Newest</option>
          <option value='oldest'>Oldest</option>
          <option value='top'>Most Liked</option>
        </Select>
      </FieldWrapper>

      <TableWrapper>
        <table className='table table-bordered table-striped'>
          <thead className='table-light'>
            <tr>
              <th role='button'>Comment</th>
              <th>User</th>
              <th role='button'>Replies</th>
              <th role='button'>Likes</th>
              <th role='button'>Date</th>
              <th>Manage</th>
            </tr>
          </thead>
          <tbody>
            {data.map((row, index) => (
              <Fragment key={index}>
                <tr key={index}>
                  <td>{row.text}</td>
                  <td>{row.user?.fullName}</td>
                  <td>
                    <div className='flex-box'>
                      <p>{row.replyCount}</p>
                      <RemoveButton
                        disabled={!Boolean(row.replyCount > 0)}
                        onClick={() =>
                          setOpenSections((state) => ({
                            ...state,
                            [row.commentId]: !Boolean(state[row.commentId])
                          }))
                        }
                        className={`collapse-button ${
                          openSections[row.commentId] ? 'opened' : 'closed'
                        }`}
                      >
                        <LeftChevron />
                      </RemoveButton>
                    </div>
                  </td>
                  <td>{row.likes}</td>
                  <td className='td-date'>
                    {moment(row.updatedAt).local().format('MM/DD/YYYY HH:mm')}
                  </td>
                  <td>
                    <a
                      className='blue'
                      role='button'
                      onClick={() => handleDelete(row.commentId)}
                    >
                      DELETE
                    </a>
                  </td>
                </tr>
                <tr>
                  <td
                    className={`p-0 ${
                      openSections[row.commentId] ? 'open' : ''
                    }`}
                    colSpan={6}
                  >
                    <Collapse isOpened={openSections[row.commentId] || false}>
                      {openSections[row.commentId] && (
                        <RepliesTable
                          comment_id={row.commentId}
                          content_id={id}
                          type={type}
                        />
                      )}
                    </Collapse>
                  </td>
                </tr>
              </Fragment>
            ))}
          </tbody>
        </table>
        <div className='text-left my-3'>
          <Button
            classID='load-more'
            variant='primary'
            onClick={handlePagination}
            disabled={!Boolean(lastKey) || loading || total === data.length}
          >
            {loading
              ? 'Loading...'
              : Boolean(lastKey) && total !== data.length
              ? 'Load More'
              : 'No More Items'}
          </Button>
        </div>
      </TableWrapper>
    </Main>
  )
}

export default ManageContent
